import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import Header from "@cloudscape-design/components/header"
import ItemCard from "@cloudscape-design/components/item-card"
import Link from "@cloudscape-design/components/link"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import { useCallback, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useInsights } from "../../hooks/queries/useDashboard"
import "./DaeDashboard.css"

const PAGE_SIZE = 5

/**
 * VerifAI panel: scrollable feed of AI-generated insights with scroll-to-load.
 */
const VerifAIPanel = () => {
  const navigate = useNavigate()
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const {
    data,
    isLoading: insightsLoading,
    isFetching: isRefreshing,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch: refreshInsights,
  } = useInsights()

  const insights = useMemo(() => data?.pages.flatMap((page) => page.insights) ?? [], [data])

  const visibleInsights = insights.slice(0, visibleCount)

  const loadMoreInsights = useCallback(() => {
    if (visibleCount < insights.length) {
      setVisibleCount((prev) => prev + PAGE_SIZE)
    }
    // Prefetch next API page when within 10 items of exhausting local buffer
    if (insights.length - visibleCount <= 10 && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [insights.length, visibleCount, hasNextPage, isFetchingNextPage, fetchNextPage])

  const scrollObserverRef = useRef<IntersectionObserver | null>(null)
  const lastInsightRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (scrollObserverRef.current) scrollObserverRef.current.disconnect()
      if (!node) return
      scrollObserverRef.current = new IntersectionObserver((entries) => {
        if (entries[0]?.isIntersecting) loadMoreInsights()
      })
      scrollObserverRef.current.observe(node)
    },
    [loadMoreInsights],
  )

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="AI-Powered Data Assistant"
          actions={
            <Button
              iconName="refresh"
              loading={isRefreshing}
              onClick={() => void refreshInsights()}
              ariaLabel="Refresh insights"
            >
              {isRefreshing ? "Refreshing..." : "Refresh"}
            </Button>
          }
        >
          VerifAI
        </Header>
      }
    >
      {insightsLoading ? (
        <Box textAlign="center" padding="l" color="text-status-inactive">
          <SpaceBetween size="s" alignItems="center">
            <Spinner />
            <span>Analyzing your data...</span>
          </SpaceBetween>
        </Box>
      ) : insights.length === 0 ? (
        <Box textAlign="center" padding="l" color="text-status-inactive">
          <SpaceBetween size="xs">
            <Box variant="strong" color="inherit">
              No insights yet.
            </Box>
            <span>Upload data files to get AI-powered analysis.</span>
          </SpaceBetween>
        </Box>
      ) : (
        <div className="dae-insight-list">
          {visibleInsights.map((insight, i) => (
            <div
              key={insight.insightId}
              ref={i === visibleInsights.length - 1 ? lastInsightRef : undefined}
            >
              <ItemCard
                variant="embedded"
                style={{ root: { borderWidth: "0", borderRadius: "0", boxShadow: "none" } }}
                footer={
                  <Button
                    variant="inline-link"
                    iconName="arrow-right"
                    iconAlign="right"
                    onClick={() => navigate(`/studies/${insight.studyId}?tab=ingestion`)}
                  >
                    View details
                  </Button>
                }
              >
                <SpaceBetween size="xs">
                  <Box>{insight.content}</Box>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                      <Link
                        href={`/studies/${insight.studyId}`}
                        onFollow={(e) => {
                          e.preventDefault()
                          navigate(`/studies/${insight.studyId}`)
                        }}
                      >
                        {insight.studyId}
                      </Link>
                      {insight.fileName && (
                        <Box color="text-body-secondary" fontSize="body-s">
                          {insight.fileName}
                        </Box>
                      )}
                    </SpaceBetween>
                    <Box color="text-status-inactive" fontSize="body-s">
                      {insight.timestamp.toLocaleDateString()}
                    </Box>
                  </div>
                </SpaceBetween>
              </ItemCard>
            </div>
          ))}
        </div>
      )}
    </Container>
  )
}

export default VerifAIPanel
